import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowLeft, Award, CalendarCheck, CalendarDays, Check, Clock, Drama, Loader2, Mic2, Music2, Sparkles, Users } from "lucide-react";
import { PageDecor } from "../components/decor/PageDecor";
import { isValidEmail } from "../lib/formHelpers";
import { useSEO } from "../lib/useSEO";
import MoisCalendrier, { moisDe } from "../components/MoisCalendrier";
import RetourApp from "../components/RetourApp";
import { CLE_JETON, espaceApi } from "../espace/api";
import { chf as chfPrive, conditionsAnnulation, ensuiteDu, franc, heures, maj, partDuPrix, phraseAbsence, regles as reglesDe, due as dueAccord } from "../lib/reglesPrives";

/* Réserver un cours privé (école, 30.10.2026 ; remplace Calendly) : la discipline, la durée,
   puis un créneau libre — avec « Tous les profs » (chaque créneau dit avec qui, une couleur
   par prof) ou un prof précis —, puis ses coordonnées. La réservation fait foi. Paiement sur
   place avant le cours, cash ou TWINT ; annulation gratuite jusqu'à 24 h avant. Le serveur
   décide de tout (créneaux, prix). Les conditions écrites ici (24 h, « la moitié du prix »…)
   sont celles des Réglages › Cours privés (`offre.regles`, Jen 02.11.2026).
   Présentation reprise de la page Calendly de la production (Jen, 30.10.2026 : « userfriendly
   est notre devise ») : en-tête, cartes Disciplines / Tarifs / Format, grandes cartes de
   choix, étapes numérotées. */

const B = process.env.REACT_APP_BACKEND_URL;
const ICONES = { danse: Music2, chant: Mic2, theatre: Drama };
const ACCROCHES = {
  danse: "Hip-hop, classique, contemporain, heels…",
  chant: "Technique vocale, interprétation, respiration.",
  theatre: "Jeu, présence scénique, texte, improvisation.",
};
const ORDRE = ["danse", "chant", "theatre"];
const JOURS = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const jourCourt = (iso) => { const d = new Date(`${iso}T12:00:00`); return { j: JOURS[d.getDay()], n: d.getDate(), m: MOIS[d.getMonth()] }; };
const JOURS_LONGS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MOIS_LONGS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const jourLong = (iso) => { const d = new Date(`${iso}T12:00:00`); return `${JOURS_LONGS[d.getDay()]} ${d.getDate()} ${MOIS_LONGS[d.getMonth()]}`; };
// Les heures d'un jour, rangées comme dans l'app Calendrier : matin, après-midi, soir.
const PERIODES = [["Matin", 0, 12 * 60], ["Après-midi", 12 * 60, 17 * 60], ["Soir", 17 * 60, 24 * 60]];
const minutesDe = (h) => { const [a, b] = h.split(":").map(Number); return a * 60 + b; };
// « 10h », « 10h30 » : comme dans les e-mails et sur la page de la réservation.
const heureFr = (h) => { const [a, b] = String(h || "").split(":"); return `${Number(a)}h${b && b !== "00" ? b : ""}`; };
const duree = (m) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${String(m % 60).padStart(2, "0")}` : ""}` : `${m} min`);

/* Le numéro d'étape et son titre, comme sur la page de la production. */
const Titre = ({ n, children }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ background: "var(--primary)" }}>{n}</div>
    <h2 className="font-display text-2xl md:text-3xl">{children}</h2>
  </div>
);

const Carte = ({ actif, onClick, children, testid, couleur }) => (
  <button type="button" onClick={onClick} data-testid={testid} className="text-left rounded-3xl p-5 transition-all"
    style={{
      background: actif ? "var(--primary-tint)" : "#fff",
      border: `2px solid ${actif ? (couleur || "var(--primary)") : "var(--border-soft)"}`,
      boxShadow: actif ? "0 10px 30px rgba(20,184,196,0.15)" : "0 4px 14px rgba(15,27,45,0.04)",
    }}>
    {children}
  </button>
);

export default function ReservationCoursPrive() {
  useSEO({
    title: "Réserver un cours privé — 1000 Arts Cugy VD",
    description: "Danse, chant ou théâtre avec le prof de ton choix : choisis ta discipline, ta durée et réserve directement un créneau libre.",
    canonical: "/inscription/cours-prive",
  });
  const [offre, setOffre] = useState(null);
  const [erreur, setErreur] = useState("");
  const [disc, setDisc] = useState("");
  const [prof, setProf] = useState("");
  const [minutes, setMinutes] = useState(0);
  const [creneaux, setCreneaux] = useState(null);
  const [jour, setJour] = useState("");
  const [choix, setChoix] = useState(null);
  const [f, setF] = useState({ pour: "moi", eleve_prenom: "", eleve_nom: "", prenom: "", nom: "", email: "", telephone: "", objectif: "" });
  const [envoi, setEnvoi] = useState(false);
  const [fait, setFait] = useState(null);
  // Ouverte depuis l'app des familles (« Réserver un cours privé », Mes cours : lien marqué
  // app=1) : « ← Retour à l'app » en haut, comme les autres pages du site ouvertes depuis l'app
  // (Jen, 01.11.2026) ; la page restait sur « Retour aux inscriptions », qui menait au site
  // (série de tests du 02.11.2026).
  const app = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("app") === "1";
  // Ouverte depuis l'app (Jen, 02.11.2026, série de tests) : le formulaire est pré-rempli avec
  // les coordonnées du compte (nom, e-mail, téléphone) et ses enfants se choisissent d'un
  // toucher. La session de l'app part avec la réservation : si la personne écrit une autre
  // adresse e-mail (celle de l'ado), le cours est quand même rattaché à son compte et apparaît
  // dans son app (le serveur le vérifie : compte à l'adresse prouvée seulement).
  const [compte, setCompte] = useState(null);
  const jetonApp = () => { try { return app ? localStorage.getItem(CLE_JETON) || "" : ""; } catch { return ""; } };
  useEffect(() => {
    if (!jetonApp()) return;
    espaceApi.get("/moi").then(({ data }) => {
      if (!data || data.verification_requise || !data.email) return;
      const c = { email: String(data.email).toLowerCase(), prenom: data.prenom_responsable || "", nom: data.nom_responsable || "",
        telephone: data.telephone || "", enfants: (data.enfants || []).map((e) => ({ id: e.id, prenom: e.prenom || "", nom: e.nom || "" })) };
      setCompte(c);
      setF((x) => ({ ...x, prenom: x.prenom || c.prenom, nom: x.nom || c.nom, email: x.email || c.email, telephone: x.telephone || c.telephone }));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const autreAdresse = compte && f.email.trim() && f.email.trim().toLowerCase() !== compte.email;
  // « Mon enfant » : le premier enfant du compte d'office (un toucher pour un autre).
  const pourQui = (v) => {
    if (v === "enfant" && compte?.enfants.length && !f.eleve_prenom.trim()) {
      const e = compte.enfants[0];
      setF({ ...f, pour: v, eleve_prenom: e.prenom, eleve_nom: e.nom });
    } else setF({ ...f, pour: v });
  };

  useEffect(() => {
    fetch(`${B}/api/cours-prives-reservation`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setOffre)
      .catch(() => setErreur("La réservation en ligne n'est pas disponible pour le moment."));
  }, []);
  const disciplines = useMemo(() => [...(offre?.disciplines || [])]
    .sort((a, b) => ORDRE.indexOf(a.id) - ORDRE.indexOf(b.id)), [offre]);
  const d = disciplines.find((x) => x.id === disc);
  // Les tarifs, une ligne par durée ; les disciplines aux mêmes prix partagent leurs lignes
  // (« Chant / Théâtre · 30 min »), comme sur la page de la production.
  const tarifs = useMemo(() => {
    const groupes = [];
    disciplines.forEach((x) => {
      const cle = JSON.stringify(x.durees);
      const g = groupes.find((y) => y.cle === cle);
      if (g) g.labels.push(x.label); else groupes.push({ cle, labels: [x.label], durees: x.durees });
    });
    return groupes;
  }, [disciplines]);
  const urlCreneaux = `${B}/api/cours-prives-reservation/creneaux?discipline=${disc}&minutes=${minutes}&prof=${prof}`;
  // Seule la réponse du DERNIER choix compte (Jen, 02.11.2026, série de tests) : la durée
  // touchée demande les créneaux de tous les profs, Lydia touchée juste après demande les siens ;
  // la première réponse, plus lente, arrivait en dernier et remplaçait l'autre — Lydia
  // surlignée, mais les heures de Noémie proposées (et réservables d'un toucher).
  useEffect(() => {
    setCreneaux(null); setJour(""); setChoix(null);
    if (!disc || !minutes) return undefined;
    let actuelle = true;
    fetch(urlCreneaux).then((r) => r.json())
      .then((x) => { if (actuelle) setCreneaux(x.creneaux || []); })
      .catch(() => { if (actuelle) setCreneaux([]); });
    return () => { actuelle = false; };
  }, [disc, minutes, prof]); // eslint-disable-line react-hooks/exhaustive-deps
  const jours = useMemo(() => [...new Set((creneaux || []).map((c) => c.date))], [creneaux]);
  useEffect(() => { if (jours.length && !jours.includes(jour)) setJour(jours[0]); }, [jours, jour]);
  // Le calendrier du mois (comme Calendly, Jen 30.10.2026) : les jours avec des créneaux en
  // vert, cliquables, une pastille par prof disponible ; les autres en gris.
  const [mois, setMois] = useState("");
  useEffect(() => { if (jours.length && (!mois || !jours.some((j) => moisDe(j) === mois))) setMois(moisDe(jours[0])); }, [jours]); // eslint-disable-line react-hooks/exhaustive-deps
  const joursCal = useMemo(() => {
    const m = {};
    (creneaux || []).forEach((c) => {
      const x = (m[c.date] ||= { points: [], heures: new Set() });
      x.heures.add(c.debut);
      if (!x.points.includes(c.couleur)) x.points.push(c.couleur);
    });
    Object.values(m).forEach((x) => { x.titre = `${x.heures.size} heure${x.heures.size > 1 ? "s" : ""} libre${x.heures.size > 1 ? "s" : ""}`; });
    return m;
  }, [creneaux]);
  // UNE heure = UN bouton (Jen, 01.11.2026) : avec « Tous les profs », 09:00 revenait une
  // fois par prof libre (09:00 Lydia, 09:00 Noémie…), une liste interminable sur téléphone.
  // Chaque heure porte la pastille des profs libres ; un seul → il est pris, plusieurs → on
  // choisit le prof juste dessous (`ouverte`).
  const heuresDuJour = useMemo(() => {
    const m = new Map();
    (creneaux || []).filter((c) => c.date === jour).forEach((c) => {
      if (!m.has(c.debut)) m.set(c.debut, { debut: c.debut, profs: [] });
      m.get(c.debut).profs.push(c);
    });
    return [...m.values()];
  }, [creneaux, jour]);
  const [ouverte, setOuverte] = useState("");
  useEffect(() => { setOuverte(""); }, [creneaux, jour]);
  const toucherHeure = (h) => {
    setErreur("");
    if (h.profs.length === 1) { setChoix(h.profs[0]); setOuverte(""); return; }
    if (choix && (choix.date !== jour || choix.debut !== h.debut)) setChoix(null);
    setOuverte(h.debut);
  };
  const prix = d?.durees.find((x) => x.minutes === minutes)?.prix;
  // Les règles de l'école (Réglages › Cours privés) : les conditions écrites sur la page.
  const R = reglesDe(offre?.regles);
  // Ce qui manque encore, dit sous le bouton (un bouton grisé sans raison laisse perplexe).
  const manque = [
    f.pour === "enfant" && !f.eleve_prenom.trim() && "le prénom de l'enfant",
    !f.prenom.trim() && (f.pour === "enfant" ? "ton prénom" : "le prénom"),
    !f.nom.trim() && "le nom",
    !isValidEmail(f.email) && "un e-mail valide",
    f.telephone.replace(/\D/g, "").length < 9 && "un numéro de téléphone",
  ].filter(Boolean);
  const pret = choix && !manque.length;
  // Une heure choisie : le formulaire s'affiche plus bas — on l'amène à l'écran (sur
  // téléphone, il restait sous la liste des heures, invisible).
  const refCoordonnees = useRef(null);
  const refCreneaux = useRef(null);
  const refDuree = useRef(null);
  const refHeures = useRef(null);
  // Chaque choix amène l'étape suivante à l'écran (retour de l'école, 02.11.2026 : « sur le
  // téléphone, on ne sait pas s'il y a une étape deux, on se croit coincé »).
  const descendreA = (ref) => setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  const choisirDiscipline = (id) => { setDisc(id); setProf(""); setMinutes(0); descendreA(refDuree); };
  const choisirDuree = (m) => { setMinutes(m); descendreA(refCreneaux); };
  useEffect(() => {
    const el = refCoordonnees.current;
    if (!choix || !el) return;
    const r = el.getBoundingClientRect();
    if (r.top > window.innerHeight - 120) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [choix]);
  // Réservé : la confirmation s'affiche en haut de la page (on était tout en bas du formulaire).
  useEffect(() => { if (fait) window.scrollTo({ top: 0 }); }, [fait]);

  const reserver = async (e) => {
    e.preventDefault();
    setErreur(""); setEnvoi(true);
    try {
      const jeton = compte ? jetonApp() : "";
      const r = await fetch(`${B}/api/cours-prives-reservation`, {
        method: "POST", headers: { "Content-Type": "application/json", ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}) },
        body: JSON.stringify({ discipline: disc, minutes, prof_id: choix.prof_id, date: choix.date, debut: choix.debut, ...f, site_web: "" }),
      });
      const x = await r.json().catch(() => ({}));
      if (r.status === 409) {
        // L'heure vient d'être prise : les heures libres sont rechargées, on en choisit une autre
        // (le formulaire garde ce qui a été écrit).
        setChoix(null);
        fetch(urlCreneaux).then((y) => y.json()).then((y) => setCreneaux(y.creneaux || [])).catch(() => {});
        setTimeout(() => refCreneaux.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
      if (!r.ok) throw new Error(x.detail || "La réservation n'a pas pu être enregistrée.");
      setFait(x);
    } catch (err) { setErreur(err.message); }
    setEnvoi(false);
  };

  if (fait) {
    return (
      <Cadre app={app}>
        <div className="card-flat text-center space-y-3" data-testid="cp-confirmation">
          <CalendarCheck className="mx-auto text-[var(--primary)]" size={40} />
          <h1 className="font-display text-3xl">C'est réservé !</h1>
          {/* « C'est réservé : chant avec Lydia, … » sous le titre « C'est réservé ! » : sans la redite. */}
          <p>{fait.message.replace(/^C'est réservé : (.)/, (_, x) => x.toUpperCase())}</p>
          <p className="text-sm">{fait.reservation.a_prevoir}</p>
          <p className="text-sm font-semibold">Paiement sur place, avant le cours : CHF {Number(prix).toFixed(2)}, en cash ou par TWINT.</p>
          <p className="text-sm text-[var(--muted)]" data-testid="cp-confirmation-conditions">Tu reçois la confirmation par e-mail, avec le cours à ajouter à ton agenda et le lien pour changer l'horaire ou annuler (gratuit jusqu'à {heures(R.annulation_heures)} avant, ensuite {ensuiteDu(R)}). {phraseAbsence(R)}</p>
          {fait.lien && (
            <a href={app ? `${fait.lien}&app=1` : fait.lien} className="btn-ghost inline-flex" data-testid="cp-gerer">
              <CalendarDays size={16} /> Changer l'horaire ou annuler
            </a>
          )}
        </div>
      </Cadre>
    );
  }
  return (
    <div data-testid="page-cours-prives" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 relative z-10">
        {app ? <RetourApp /> : (
          <Link to="/inscription" className="btn-ghost mb-6 inline-flex items-center gap-1">
            <ArrowLeft size={14} /> Retour aux inscriptions
          </Link>
        )}

        <div className="eyebrow mb-3">Cours privés · Réservation en ligne</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-tight">
          Réserve ton créneau<br /><span style={{ color: "var(--primary)" }}>de cours privé</span>
        </h1>
        <p className="mt-6 text-lg text-[var(--muted)] max-w-2xl">
          Choisis ta discipline et la durée qui te correspond, puis un créneau libre — avec le prof de ton choix, ou celui qui est disponible.
        </p>

        {erreur && !offre && <div className="card-flat mt-8">{erreur}</div>}
        {!offre && !erreur && <div className="py-12 grid place-items-center"><Loader2 className="animate-spin" /></div>}
        {offre && (
          <>
            {/* Disciplines, tarifs et format, d'un coup d'œil */}
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              <div className="card-flat" data-testid="cp-info-disciplines">
                <div className="flex items-center gap-2 mb-3" style={{ color: "var(--primary)" }}>
                  <Sparkles size={16} /> <span className="eyebrow text-[10px]">Disciplines</span>
                </div>
                <p className="text-base font-medium">{disciplines.map((x) => x.label).join(" · ")}.</p>
              </div>
              <div className="card-flat" data-testid="cp-info-tarifs">
                <div className="flex items-center gap-2 mb-3" style={{ color: "var(--primary)" }}>
                  <Award size={16} /> <span className="eyebrow text-[10px]">Tarifs</span>
                </div>
                <ul className="text-sm space-y-1.5">
                  {tarifs.map((g, i) => g.durees.map((x, k) => (
                    <li key={`${g.cle}-${x.minutes}`} className="flex items-baseline justify-between gap-3"
                      style={i > 0 && k === 0 ? { borderTop: "1px solid var(--border-soft)", paddingTop: 6 } : undefined}>
                      <span className="text-[var(--text)]">{g.labels.join(" / ")} · {duree(x.minutes)}</span>
                      <strong style={{ color: "var(--primary)" }}>CHF {franc(x.prix)}</strong>
                    </li>
                  )))}
                </ul>
              </div>
              <div className="card-flat" data-testid="cp-info-format">
                <div className="flex items-center gap-2 mb-3" style={{ color: "var(--primary)" }}>
                  <Clock size={16} /> <span className="eyebrow text-[10px]">Format</span>
                </div>
                <p className="text-base font-medium">Cours individuel, dans nos salles à Cugy VD.</p>
                <p className="text-sm text-[var(--muted)] mt-2" data-testid="cp-info-conditions">Paiement sur place avant le cours (cash ou TWINT). Annulation gratuite jusqu'à {heures(R.annulation_heures)} avant. {phraseAbsence(R)}</p>
              </div>
            </div>

            {/* Étape 1 — la discipline */}
            <section className="mt-12" data-testid="cp-disciplines">
              <Titre n={1}>Quelle discipline&nbsp;?</Titre>
              <div className="grid md:grid-cols-3 gap-4">
                {disciplines.map((x) => {
                  const Icone = ICONES[x.id] || Music2;
                  const actif = disc === x.id;
                  return (
                    <button key={x.id} type="button" onClick={() => choisirDiscipline(x.id)} data-testid={`cp-discipline-${x.id}`}
                      className="text-left rounded-3xl p-6 transition-all"
                      style={{
                        background: actif ? "var(--primary)" : "#fff", color: actif ? "#fff" : "var(--text)",
                        border: actif ? "2px solid var(--primary)" : "2px solid var(--border-soft)",
                        boxShadow: actif ? "0 14px 40px rgba(20,184,196,0.25)" : "0 4px 14px rgba(15,27,45,0.05)",
                        transform: actif ? "translateY(-2px)" : "none",
                      }}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
                          style={{ background: actif ? "rgba(255,255,255,0.2)" : "var(--primary-tint)", color: actif ? "#fff" : "var(--primary)" }}>
                          <Icone size={22} />
                        </div>
                        <div className="font-display text-xl">{x.label}</div>
                      </div>
                      <p className="text-sm" style={{ color: actif ? "rgba(255,255,255,0.85)" : "var(--muted)" }}>{ACCROCHES[x.id] || ""}</p>
                    </button>
                  );
                })}
              </div>
              {/* Le texte de l'école sur ce cours, une fois la discipline choisie */}
              {d?.description && (
                <p className="mt-4 text-[15px] leading-relaxed rounded-3xl p-5" style={{ background: "var(--primary-tint)" }} data-testid="cp-description">
                  {d.description}
                </p>
              )}
            </section>

            {/* Étape 2 — la durée */}
            {d && (
              <section className="mt-12 scroll-mt-28" data-testid="cp-prof-duree" ref={refDuree}>
                <Titre n={2}>Quelle durée&nbsp;?</Titre>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {d.durees.map((x) => (
                    <Carte key={x.minutes} actif={minutes === x.minutes} onClick={() => choisirDuree(x.minutes)} testid={`cp-duree-${x.minutes}`}>
                      <div className="flex items-center gap-2 mb-2" style={{ color: "var(--primary)" }}>
                        <Clock size={16} /> <span className="eyebrow text-[10px]">Durée</span>
                      </div>
                      <div className="font-display text-xl mb-1">{duree(x.minutes)}</div>
                      <div className="text-sm font-semibold" style={{ color: "var(--primary)" }}>CHF {franc(x.prix)}</div>
                    </Carte>
                  ))}
                </div>
              </section>
            )}

            {/* Étape 3 — le créneau, avec qui */}
            {d && minutes > 0 && (
              <section className="mt-12 scroll-mt-28" data-testid="cp-creneaux" ref={refCreneaux}>
                <Titre n={3}>Choisis ton créneau</Titre>
                <div className="rounded-3xl overflow-hidden shadow-lg bg-white">
                  <div className="flex flex-wrap items-center gap-2 px-6 py-3 text-sm font-medium" style={{ background: "var(--primary)", color: "#fff" }}>
                    <CalendarDays size={16} /> {d.label} · {duree(minutes)} · CHF {franc(prix)}
                  </div>
                  <div className="p-5 md:p-6 space-y-5">
                    {/* Avec qui : tous les profs (chacun sa couleur) ou un prof précis */}
                    {d.profs.length > 1 && (
                      <div>
                        <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Users size={15} /> Avec qui&nbsp;?</div>
                        <div className="flex flex-wrap gap-2" data-testid="cp-profs">
                          <button type="button" onClick={() => setProf("")} data-testid="cp-prof-tous"
                            className="rounded-full border px-4 py-2 text-sm font-medium"
                            style={!prof ? { background: "var(--primary)", borderColor: "var(--primary)", color: "#fff" } : { borderColor: "var(--border)", background: "#fff" }}>
                            Tous les profs disponibles
                          </button>
                          {d.profs.map((p) => (
                            <button key={p.id} type="button" onClick={() => setProf(p.id)} data-testid={`cp-prof-${p.id}`}
                              className="rounded-full border px-4 py-2 text-sm font-medium inline-flex items-center gap-2"
                              style={prof === p.id ? { background: p.couleur, borderColor: p.couleur, color: "#fff" } : { borderColor: "var(--border)", background: "#fff" }}>
                              <span className="w-2.5 h-2.5 rounded-full" style={{ background: prof === p.id ? "#fff" : p.couleur }} /> {p.prenom}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {erreur && !choix && (
                      <p className="text-sm flex gap-2" style={{ color: "#B91C1C" }} data-testid="cp-erreur-creneau"><AlertCircle size={16} className="shrink-0 mt-0.5" /> {erreur}</p>
                    )}
                    {!creneaux ? <Loader2 className="animate-spin" /> : !creneaux.length ? (
                      <p className="text-sm text-[var(--muted)]">Aucun créneau libre pour l'instant{prof ? " avec ce prof" : ""}.{prof ? " Essaie « Tous les profs disponibles »," : ""} ou écris-nous à info1000arts@gmail.com.</p>
                    ) : (
                      <>
                        <div className="grid md:grid-cols-[minmax(0,1fr)_290px] gap-6 md:gap-8">
                          <div>
                            <div className="text-sm font-semibold mb-3">Quel jour&nbsp;? <span className="font-normal text-[var(--muted)]">Les jours en vert ont des créneaux libres.</span></div>
                            {mois && (
                              <MoisCalendrier mois={mois} onMois={setMois} jours={joursCal} choisi={jour} testid="cp-jours"
                                onChoisir={(j) => {
                                  setJour(j); setChoix(null);
                                  // Sur téléphone, les heures sont sous le calendrier : on y descend.
                                  if (window.innerWidth < 768) descendreA(refHeures);
                                }} min={jours[0]} max={jours[jours.length - 1]} />
                            )}
                          </div>
                          <div className="scroll-mt-24" ref={refHeures}>
                            <div className="text-sm font-semibold mb-3">{jour ? jourLong(jour).charAt(0).toUpperCase() + jourLong(jour).slice(1) : "À quelle heure ?"}</div>
                            <div className="space-y-4 md:max-h-[380px] md:overflow-y-auto md:pr-1" data-testid="cp-heures">
                              {PERIODES.map(([nom, de, a]) => {
                                const liste = heuresDuJour.filter((h) => minutesDe(h.debut) >= de && minutesDe(h.debut) < a);
                                if (!liste.length) return null;
                                // Le choix du prof se glisse sous la RANGÉE de l'heure touchée (deux
                                // heures par rangée), sans décaler sa voisine.
                                const i = liste.findIndex((h) => h.debut === ouverte);
                                const finRangee = i < 0 ? -1 : Math.min(i + (i % 2 === 0 ? 1 : 0), liste.length - 1);
                                return (
                                  <div key={nom}>
                                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[var(--muted)] mb-1.5">{nom}</div>
                                    <div className="grid grid-cols-2 gap-2">
                                      {liste.map((h, k) => {
                                        const pris = choix && choix.date === jour && choix.debut === h.debut ? choix : null;
                                        const coul = pris ? pris.couleur : "var(--primary)";
                                        const surligne = pris || ouverte === h.debut;
                                        const plusieurs = h.profs.length > 1;
                                        return (
                                          <React.Fragment key={h.debut}>
                                            <button type="button" onClick={() => toucherHeure(h)} data-testid={`cp-creneau-${h.debut}`}
                                              aria-expanded={plusieurs ? ouverte === h.debut : undefined}
                                              className="rounded-xl border-2 px-2.5 py-2 text-left transition-colors"
                                              style={{ borderColor: surligne ? coul : "var(--border)",
                                                       background: pris ? `${pris.couleur}1A` : ouverte === h.debut ? "var(--primary-tint)" : "#fff" }}>
                                              <span className="font-semibold flex items-center gap-1 tabular-nums">{pris && <Check size={13} />}{h.debut}</span>
                                              <span className="text-[11.5px] text-[var(--muted)] flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                                                {(pris ? [pris] : h.profs).map((c) => (
                                                  <span key={c.prof_id} className="inline-flex items-center gap-1">
                                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.couleur }} /> {c.prof}
                                                  </span>
                                                ))}
                                              </span>
                                            </button>
                                            {k === finRangee && (
                                              <div className="col-span-2 rounded-2xl p-3 space-y-2" style={{ background: "var(--primary-tint)" }} data-testid="cp-choix-prof">
                                                <div className="text-[13px] font-semibold">À {ouverte}, avec qui&nbsp;?</div>
                                                <div className="flex flex-wrap gap-2">
                                                  {liste[i].profs.map((c) => (
                                                    <button key={c.prof_id} type="button" data-testid={`cp-choix-prof-${c.prof_id}`}
                                                      onClick={() => { setChoix(c); setErreur(""); setOuverte(""); }}
                                                      className="min-h-[44px] rounded-full border-2 bg-white px-4 text-sm font-semibold inline-flex items-center gap-2 active:scale-95 transition-transform"
                                                      style={{ borderColor: c.couleur, touchAction: "manipulation" }}>
                                                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.couleur }} /> {c.prof}
                                                    </button>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Étape 4 — les coordonnées */}
            {choix && (
              <section className="mt-12 scroll-mt-28" data-testid="cp-coordonnees" ref={refCoordonnees}>
                <Titre n={4}>Tes coordonnées</Titre>
                <form onSubmit={reserver} noValidate className="card-flat space-y-5">
                  <div className="rounded-2xl p-4 text-sm space-y-1" style={{ background: "var(--primary-tint)" }} data-testid="cp-recap">
                    <div className="text-base"><strong>{d.label} avec {choix.prof}</strong> · {jourCourt(choix.date).j} {jourCourt(choix.date).n} {jourCourt(choix.date).m} à {choix.debut} ({duree(minutes)})</div>
                    <div><strong>Paiement sur place, avant le cours : CHF {Number(prix).toFixed(2)}</strong>, en cash ou par TWINT.</div>
                    <div className="text-[var(--muted)]" data-testid="cp-recap-conditions">{conditionsAnnulation(R)}</div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold mb-2">Le cours est pour…</div>
                    <div className="flex flex-wrap gap-2">
                      {[["moi", "Moi"], ["enfant", "Mon enfant"]].map(([v, l]) => (
                        <button key={v} type="button" onClick={() => pourQui(v)} data-testid={v === "enfant" ? "cp-pour-enfant" : "cp-pour-moi"}
                          className="rounded-full border px-4 py-2 text-sm font-medium"
                          style={f.pour === v ? { background: "var(--primary)", borderColor: "var(--primary)", color: "#fff" } : { borderColor: "var(--border)", background: "#fff" }}>{l}</button>
                      ))}
                    </div>
                  </div>
                  {compte && (
                    <p className="text-xs text-[var(--muted)] -mt-2" data-testid="cp-compte">Pré-rempli avec les coordonnées de ton compte ({compte.email}).</p>
                  )}
                  {/* Les enfants du compte, d'un toucher. */}
                  {f.pour === "enfant" && compte?.enfants.length > 0 && (
                    <div className="flex flex-wrap gap-2 -mt-2" data-testid="cp-enfants">
                      {compte.enfants.map((e) => {
                        const on = f.eleve_prenom === e.prenom && f.eleve_nom === e.nom;
                        return (
                          <button key={e.id} type="button" onClick={() => setF({ ...f, eleve_prenom: e.prenom, eleve_nom: e.nom })} data-testid={`cp-enfant-${e.id}`}
                            className="rounded-full border px-3.5 py-1.5 text-sm font-medium inline-flex items-center gap-1.5"
                            style={on ? { background: "var(--primary-tint)", borderColor: "var(--primary)" } : { borderColor: "var(--border)", background: "#fff" }}>
                            {on && <Check size={13} />}{e.prenom}
                          </button>
                        );
                      })}
                      <button type="button" onClick={() => setF({ ...f, eleve_prenom: "", eleve_nom: "" })} data-testid="cp-enfant-autre"
                        className="rounded-full border px-3.5 py-1.5 text-sm font-medium" style={{ borderColor: "var(--border)", background: "#fff" }}>Un autre enfant</button>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {f.pour === "enfant" && [["eleve_prenom", "Prénom de l'enfant"], ["eleve_nom", "Nom de l'enfant"]].map(([k, l]) => (
                      <label key={k} className="block"><span className="field-label">{l}</span>
                        <input className="input-field" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cp-${k}`} /></label>
                    ))}
                    {[["prenom", f.pour === "enfant" ? "Ton prénom (parent)" : "Prénom"], ["nom", "Nom"], ["email", "E-mail"], ["telephone", "Téléphone"]].map(([k, l]) => (
                      <label key={k} className="block"><span className="field-label">{l}</span>
                        <input className="input-field" type={k === "email" ? "email" : k === "telephone" ? "tel" : "text"} value={f[k]}
                          onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cp-${k}`} />
                        {/* Une autre adresse que celle du compte : où partent les e-mails, et que le
                            cours reste dans l'app (rattaché au compte). */}
                        {k === "email" && autreAdresse && (
                          <span className="block text-xs mt-1.5 rounded-xl px-3 py-2" style={{ background: "#FFFBEB", color: "#92400E" }} data-testid="cp-email-autre">
                            La confirmation et le lien pour changer l'horaire partiront à cette adresse. Le cours apparaîtra quand même dans ton app : il est rattaché à ton compte ({compte.email}).
                          </span>
                        )}</label>
                    ))}
                    <label className="block sm:col-span-2"><span className="field-label">Ce que tu aimerais travailler (facultatif)</span>
                      <textarea className="input-field" rows={2} value={f.objectif} onChange={(e) => setF({ ...f, objectif: e.target.value })} /></label>
                  </div>
                  {erreur && <p className="text-sm flex gap-2" style={{ color: "#B91C1C" }}><AlertCircle size={16} /> {erreur}</p>}
                  <button type="submit" className="btn-primary" disabled={!pret || envoi} data-testid="cp-reserver">
                    {envoi ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />} Réserver ce cours
                  </button>
                  {manque.length > 0 && (
                    <p className="text-xs text-[var(--muted)]" data-testid="cp-manque">Pour réserver, indique encore {manque.length > 1 ? `${manque.slice(0, -1).join(", ")} et ${manque[manque.length - 1]}` : manque[0]}.</p>
                  )}
                </form>
              </section>
            )}
          </>
        )}

        <p className="mt-8 text-xs text-[var(--muted)] text-center">
          Alternative : envoie-nous directement un mail à{" "}
          <a href="mailto:info1000arts@gmail.com" className="underline" style={{ color: "var(--primary)" }}>info1000arts@gmail.com</a>{" "}
          si tu ne trouves pas de créneau qui te convient.
        </p>
      </div>
    </div>
  );
}

// `app` : la page est ouverte depuis l'application des familles (lien marqué `app=1`) : en
// haut, « ← Retour à l'app » (RetourApp) au lieu de « Retour aux inscriptions ».
const Cadre = ({ children, app = false }) => (
  <div data-testid="page-cours-prives" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
    <PageDecor />
    <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10 space-y-6">
      {app ? <RetourApp />
        : <Link to="/inscription" className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Retour aux inscriptions</Link>}
      {children}
    </div>
  </div>
);

/* /mon-cours-prive?id=…&j=… : le lien de l'e-mail de confirmation, pour changer l'horaire
   ou annuler. Un cours annulé propose d'en réserver un autre ; un cours passé ne propose plus
   rien (Jen, 01.11.2026 : tester chaque bouton, « user friendly »). */
export function MonCoursPrive() {
  useSEO({ title: "Mon cours privé — 1000 ARTS", description: "Ta réservation de cours privé.", canonical: "/mon-cours-prive" });
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const id = params.get("id") || "";
  const j = params.get("j") || "";
  const app = params.get("app") === "1";
  const [r, setR] = useState(null);
  // Qui appeler : le prof du cours et Noémie (numéros de leurs fiches), l'e-mail de l'école —
  // comme les e-mails (Jen, 02.11.2026 au soir). Arrivés avec la réservation.
  const [contact, setContact] = useState(null);
  // Les règles de l'école (Réglages › Cours privés), arrivées avec la réservation.
  const [reglesLues, setReglesLues] = useState(null);
  const R = reglesDe(reglesLues);
  const [message, setMessage] = useState("");
  const [erreur, setErreur] = useState("");
  const [confirmer, setConfirmer] = useState(false);
  // Changer l'horaire (Jen, 31.10.2026 : « seulement annuler, et non changer l'horaire ? ») :
  // le mois avec les jours libres du même prof, puis l'heure.
  const [changer, setChanger] = useState(false);
  const [libres, setLibres] = useState(null);
  const [mois, setMois] = useState("");
  const [jour, setJour] = useState("");
  // Jen, 01.11.2026 : toucher une heure la CHOISIT ; « Confirmer le changement » la valide.
  const [choisi, setChoisi] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  // L'heure choisie : « Confirmer le changement » est amené à l'écran (sur téléphone, il
  // restait sous la liste des heures et rien ne semblait se passer).
  const refRecap = useRef(null);
  useEffect(() => { if (choisi) refRecap.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [choisi]);
  const refErreur = useRef(null);
  useEffect(() => { if (erreur && changer) refErreur.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [erreur, changer]);
  const url = (suite = "") => `${B}/api/cours-prives-reservation/${encodeURIComponent(id)}${suite}?j=${encodeURIComponent(j)}`;
  // Sans connexion, le navigateur répond en anglais (« Failed to fetch », « Load failed ») : on
  // le dit en français ; un lien sans numéro ni code n'interroge pas le serveur (série de tests
  // du 02.11.2026 : « Failed to fetch » s'affichait sur /mon-cours-prive tout court).
  const horsLigne = (e, texte) => (e instanceof TypeError ? `${texte} : vérifie ta connexion et réessaie.` : e.message);
  useEffect(() => {
    if (!id || !j) { setErreur("Réservation introuvable."); return; }
    fetch(url())
      .then(async (x) => { const d = await x.json().catch(() => ({})); if (!x.ok) throw new Error(d.detail || "Réservation introuvable."); setR(d); if (d.regles) setReglesLues(d.regles); if (d.contact) setContact(d.contact); })
      .catch((e) => setErreur(horsLigne(e, "Ta réservation ne s'affiche pas")));
  }, [id, j]); // eslint-disable-line react-hooks/exhaustive-deps
  // Un seul envoi à la fois (Jen, 02.11.2026, série de tests) : un double toucher sur « Oui,
  // annuler le cours » envoyait deux annulations ; le serveur n'en garde qu'une, mais la
  // seconde revenait en rouge (« Cette réservation est déjà annulée. ») sous le message vert.
  const enCours = useRef(false);
  const annuler = async () => {
    if (enCours.current) return;
    enCours.current = true; setEnvoi(true); setErreur("");
    try {
      const x = await fetch(url("/annuler"), { method: "POST" });
      const d = await x.json().catch(() => ({}));
      if (x.ok) { setR(d.reservation); setMessage(d.message); } else setErreur(d.detail || "Annulation impossible.");
    } catch {
      setErreur("Annulation impossible : vérifie ta connexion et réessaie.");
    } finally {
      enCours.current = false; setEnvoi(false);
    }
  };
  // `garder` : on recharge les heures libres sans quitter le jour choisi (après un refus).
  const chargerLibres = async (garder = false) => {
    const x = await fetch(url("/creneaux"));
    const d = await x.json().catch(() => ({}));
    if (!x.ok) throw new Error(d.detail || "Créneaux introuvables.");
    setLibres(d.creneaux || []);
    if (garder) return;
    const premier = (d.creneaux || [])[0];
    setMois(premier ? moisDe(premier.date) : moisDe(r.date)); setJour(""); setChoisi(null);
  };
  const ouvrirChanger = async () => {
    setErreur(""); setMessage(""); setChanger(true); setConfirmer(false); setLibres(null);
    try { await chargerLibres(); } catch (e) { setErreur(horsLigne(e, "Les créneaux ne s'affichent pas")); setChanger(false); }
  };
  const deplacer = async (c) => {
    setEnvoi(true); setErreur("");
    try {
      const x = await fetch(url("/deplacer"), { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: c.date, debut: c.debut }) });
      const d = await x.json().catch(() => ({}));
      if (x.ok) { setR(d); if (d.regles) setReglesLues(d.regles); setMessage(d.message); setChanger(false); setChoisi(null); return; }
      setErreur(d.detail || "Changement impossible.");
      // L'heure vient d'être prise par quelqu'un d'autre : elle disparaît de la liste (elle
      // restait proposée, série de tests du 02.11.2026).
      if (x.status === 409) { setChoisi(null); await chargerLibres(true).catch(() => {}); }
    } catch (e) {
      setErreur(horsLigne(e, "Changement impossible"));
    } finally {
      setEnvoi(false);
    }
  };
  const parJour = useMemo(() => {
    const m = {};
    (libres || []).forEach((c) => { (m[c.date] ||= []).push(c); });
    return m;
  }, [libres]);
  const joursCal = useMemo(() => Object.fromEntries(Object.keys(parJour).map((d) => [d, { points: [] }])), [parJour]);
  const passe = r && new Date(`${r.date}T${r.debut}:00`).getTime() <= Date.now();
  // « Lydia · 079 … · Noémie · 078 … · info1000arts@gmail.com » : un numéro absent n'est pas écrit.
  const personnes = (contact?.personnes || []).filter((p) => p && p.telephone);
  const email = contact?.email || "info1000arts@gmail.com";
  const ecole = (
    <span data-testid="mon-cours-prive-contact">
      {personnes.map((p) => (
        <React.Fragment key={`${p.prenom}-${p.telephone}`}>
          {p.prenom} · <a href={`tel:${String(p.telephone).replace(/[^\d+]/g, "")}`} className="underline whitespace-nowrap">{p.telephone}</a>{" · "}
        </React.Fragment>
      ))}
      <a href={`mailto:${email}`} className="underline">{email}</a>
    </span>
  );
  return (
    <Cadre app={app}>
      {erreur && !changer && <div className="card-flat mb-4" style={{ color: "#B91C1C" }}>{erreur}</div>}
      {r && (
        <div className="card-flat space-y-3" data-testid="mon-cours-prive">
          <h1 className="font-display text-3xl">Ton cours privé</h1>
          <p><strong>{r.discipline_label} avec {r.prof}</strong>{r.pour === "enfant" && r.eleve_prenom ? ` pour ${r.eleve_prenom}` : ""}, {r.quand} ({r.minutes} min){r.statut === "annulee" ? "" : ` · CHF ${Number(r.montant).toFixed(2)} sur place`}.</p>
          {message && <p className="text-sm" style={{ color: "#15803D" }} data-testid="mon-cours-prive-message">{message}</p>}
          {r.statut === "annulee" ? (
            <div className="space-y-3" data-testid="mon-cours-prive-annule">
              {!message && <p className="font-semibold">Ce cours est annulé.{r.tardive ? ` ${maj(partDuPrix(r.tardive_pourcentage || 50).texte)} (${chfPrive((Number(r.montant) * (r.tardive_pourcentage || 50)) / 100)}) reste ${dueAccord(partDuPrix(r.tardive_pourcentage || 50).f)}.` : ""}</p>}
              <Link to="/inscription/cours-prive" className="btn-primary inline-flex" data-testid="mon-cours-prive-reserver"><CalendarCheck size={16} /> Réserver un autre cours</Link>
            </div>
          ) : passe ? (
            <p className="text-sm text-[var(--muted)]" data-testid="mon-cours-prive-passe">
              {r.absent ? `Absent·e sans avoir annulé : ${partDuPrix(r.absent_pourcentage || 50).texte} (CHF ${(Number(r.montant) * (r.absent_pourcentage || 50) / 100).toFixed(2)}) est facturé${partDuPrix(r.absent_pourcentage || 50).f ? "e" : ""}, la facture t'a été envoyée par e-mail. `
                : "Ce cours a déjà commencé ou a eu lieu. "}Une question ? {ecole}.</p>
          ) : (
            <>
              {!changer && !confirmer && (
                <div className="flex flex-wrap gap-2">
                  {r.annulable_sans_frais && (
                    <button className="btn-primary" onClick={ouvrirChanger} data-testid="mon-cours-prive-changer">
                      <CalendarDays size={16} /> Changer l'horaire
                    </button>
                  )}
                  <button className="btn-ghost" onClick={() => setConfirmer(true)} data-testid="mon-cours-prive-annuler">Annuler mon cours</button>
                </div>
              )}
              {!r.annulable_sans_frais && !confirmer && (
                <p className="text-sm text-[var(--muted)]" data-testid="mon-cours-prive-tard">Moins de {heures(R.annulation_heures)} avant le cours, l'horaire ne se change plus en ligne : contacte-nous ({ecole}).</p>
              )}
              {changer && (
                <div className="rounded-2xl border p-4 space-y-4" style={{ borderColor: "var(--border)" }} data-testid="mon-cours-prive-creneaux">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <div className="font-semibold">Choisis un autre moment avec {r.prof}</div>
                    <button className="text-sm font-medium" style={{ color: "var(--primary)" }} onClick={() => { setChanger(false); setChoisi(null); setErreur(""); }} data-testid="mcp-garder">Garder mon horaire</button>
                  </div>
                  {/* Un refus (l'heure vient d'être prise) se dit ICI, sous les yeux : en haut de la
                      page, on ne le voyait pas sur téléphone (série de tests, 02.11.2026). */}
                  {erreur && <p ref={refErreur} className="text-sm rounded-xl px-3 py-2 scroll-mt-24" style={{ color: "#B91C1C", background: "#FEF2F2" }} data-testid="mcp-erreur">{erreur}</p>}
                  {!libres ? <p className="text-sm text-[var(--muted)]">Chargement…</p> : !libres.length ? (
                    <p className="text-sm text-[var(--muted)]">Pas d'autre créneau libre pour l'instant : contacte l'école.</p>
                  ) : (
                    <div className="grid md:grid-cols-[300px_1fr] gap-4 items-start">
                      <MoisCalendrier mois={mois} onMois={setMois} jours={joursCal} choisi={jour} onChoisir={(x) => { setJour(x); setChoisi(null); }} testid="mcp-jours"
                        min={Object.keys(parJour)[0]} max={Object.keys(parJour).slice(-1)[0]} />
                      <div className="flex flex-wrap gap-2 content-start">
                        {!jour ? <p className="text-sm text-[var(--muted)]">Choisis un jour en vert.</p>
                          : (parJour[jour] || []).map((c) => (
                            <button key={c.debut} type="button" disabled={envoi} onClick={() => setChoisi(c)} data-testid="mcp-heure"
                              className="rounded-full border px-4 py-2 text-sm font-semibold hover:bg-[var(--primary-tint)]"
                              style={choisi && choisi.date === c.date && choisi.debut === c.debut
                                ? { background: "var(--primary)", borderColor: "var(--primary)", color: "#04202A" } : { borderColor: "var(--border)" }}>
                              {c.debut}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                  {choisi && (
                    <div ref={refRecap} className="rounded-2xl p-4 space-y-3 scroll-mb-4" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }} data-testid="mcp-recap">
                      <p className="text-sm">Ton cours passe du <strong>{r.quand}</strong> au <strong>{jourLong(choisi.date)} à {heureFr(choisi.debut)}</strong>.</p>
                      <div className="flex flex-wrap gap-2">
                        <button className="btn-primary" disabled={envoi} onClick={() => deplacer(choisi)} data-testid="mcp-confirmer">
                          {envoi ? "Un instant…" : "Confirmer le changement"}</button>
                        <button className="btn-ghost" onClick={() => setChoisi(null)}>Choisir une autre heure</button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-[var(--muted)]">Gratuit jusqu'à {heures(R.annulation_heures)} avant le cours. Toi et {r.prof} recevez un e-mail avec le nouvel horaire.</p>
                </div>
              )}
              {confirmer && (
                <div className="rounded-2xl p-4 space-y-3" style={{ background: r.annulable_sans_frais ? "#F0FDF4" : "#FEF2F2" }}>
                  <p className="text-sm" data-testid="mon-cours-prive-frais">{r.annulable_sans_frais ? `L'annulation est gratuite (plus de ${heures(R.annulation_heures)} avant le cours).`
                    : `Le cours est dans moins de ${heures(R.annulation_heures)} : ${ensuiteDu(R, r.montant)}.`}</p>
                  <div className="flex flex-wrap gap-2">
                    <button className="btn-primary" onClick={annuler} disabled={envoi} data-testid="mon-cours-prive-confirmer">{envoi ? "Un instant…" : "Oui, annuler le cours"}</button>
                    <button className="btn-ghost" onClick={() => setConfirmer(false)}>Non, garder mon cours</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Cadre>
  );
}
